import { useTranslation } from 'react-i18next';
import { Skeleton } from '@shared/ui';
import { cn } from '@shared/lib/utils';
import { logo as logoLightOnDark } from '@shared/assets';
import logoDarkOnLight from '@shared/assets/icons/logo-dark.svg';
import { useInvoiceTheme } from '../lib/invoice-theme';

/**
 * Full-widget first-paint shell: matches Card A + Card B geometry
 * so expired/success/payment do not jump the centered viewport.
 */
export function InvoicePaySkeleton() {
  const { t } = useTranslation('invoice-pay');
  const { theme } = useInvoiceTheme();
  const logoSrc = theme === 'dark' ? logoLightOnDark : logoDarkOnLight;

  return (
    <div
      className="mx-auto w-full max-w-[540px]"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">{t('loading')}</span>

      <section
        className={cn(
          'w-full rounded-t-xl border shadow-sm',
          'border-[color:var(--custom-outline)]',
          'border-t-[color:var(--base-input)]',
          'bg-[var(--base-sidebar-background)]',
          'px-5 pb-7 pt-9 sm:px-9'
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <img
            src={logoSrc}
            alt="paygatex"
            width={170}
            height={24}
            className="h-6 w-[170px] object-contain object-left"
          />
          <div className="flex h-5 w-14 items-center gap-4">
            <Skeleton className="size-5 rounded-sm" />
            <Skeleton className="size-5 rounded-sm" />
          </div>
        </div>

        <div className="my-7 h-px w-full bg-[var(--base-input)]" aria-hidden />

        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-[70%] max-w-[280px] rounded-lg" />
          <Skeleton className="h-5 w-28 rounded-md" />
          <Skeleton className="mt-1 h-5 w-44 rounded-md" />
        </div>
      </section>

      <section
        className={cn(
          'flex min-h-[272px] w-full flex-col justify-center rounded-b-xl border-x border-b border-t-0 bg-background shadow-sm',
          'border-[color:var(--custom-outline)]',
          'px-5 pb-9 pt-7 sm:px-9'
        )}
      >
        <div className="flex flex-col items-center gap-6">
          <Skeleton className="size-12 rounded-full" />
          <div className="flex w-full flex-col items-center gap-2">
            <Skeleton className="h-5 w-3/4 max-w-[280px] rounded-md" />
            <Skeleton className="h-4 w-1/2 max-w-[200px] rounded-md" />
          </div>
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      </section>

      <div className="mt-6 flex justify-center sm:mt-7">
        <Skeleton className="h-5 w-64 max-w-full rounded-md" />
      </div>
    </div>
  );
}
